import { useCallback, useEffect, useState } from 'react';
import { AvatarView } from '../avatar/AvatarView';
import { Bubble } from '../components/Bubble';
import { useBackLayer } from '../hooks/useBackLayer';
import { type PublicPlayer, basicAvatar, rankLabel } from '../models/types';
import { api } from '../services/api';
import { ApiException } from '../services/apiClient';
import { useShell } from '../state/shell';
import { colors, withAlpha } from '../theme';

type Load = { kind: 'loading' } | { kind: 'data'; player: PublicPlayer } | { kind: 'notFound' } | { kind: 'error' };

/** Another hunter's read-only profile, led by their avatar. Opened from their row on the Ranks tab. */
export function PlayerProfileScreen({ playerId, rank, displayName }: { playerId: string; rank?: number; displayName?: string }) {
  const close = useShell((s) => s.closeProfile);
  useBackLayer(close);
  const [state, setState] = useState<Load>({ kind: 'loading' });

  // No automatic retries: a 404 will not change, and the screen offers "Try again".
  const fetchProfile = useCallback(() => {
    let live = true;
    setState({ kind: 'loading' });
    api
      .getPublicPlayer(playerId)
      .then((player) => live && setState({ kind: 'data', player }))
      .catch((e) => live && setState(e instanceof ApiException && e.isPlayerNotFound ? { kind: 'notFound' } : { kind: 'error' }));
    return () => {
      live = false;
    };
  }, [playerId]);

  useEffect(() => fetchProfile(), [fetchProfile]);

  const title = state.kind === 'data' ? state.player.displayName : (displayName ?? 'Hunter');

  return (
    <div className="screen" style={{ position: 'absolute', inset: 0, background: 'var(--cream)', zIndex: 10 }} role="region" aria-label={`${title}'s profile`}>
      <div className="app-bar">
        <button type="button" className="icon-btn back" aria-label="Back" onClick={close}>←</button>
        <h1 style={{ font: 'inherit' }}>{title}</h1>
      </div>
      <div className="scroll">
        {state.kind === 'data' ? (
          <div className="stack">
            <Bubble style={{ textAlign: 'center' }}>
              <AvatarView avatar={state.player.avatar} size={280} flippable />
              <h2 className="t-headline" style={{ fontWeight: 900, marginTop: 8 }}>{state.player.displayName}</h2>
              {rank != null && <span className="chip" style={{ background: colors.lemon, marginTop: 8 }}>{rankLabel(rank)}</span>}
            </Bubble>
            <Bubble color={withAlpha(colors.lemon, 0.5)}>
              <div className="t-title-m">🎒 {state.player.score} found</div>
              <div className="t-title-m" style={{ marginTop: 8 }}>🧩 {state.player.huntsCompleted} hunts completed</div>
            </Bubble>
          </div>
        ) : state.kind === 'notFound' ? (
          <Message emoji="🍃" text="This hunter has wandered off." />
        ) : state.kind === 'error' ? (
          <Message emoji="🫧" text="Couldn't load this hunter right now." action={<button type="button" className="btn filled" onClick={fetchProfile}>Try again</button>} />
        ) : (
          <div className="center-fill pulse"><AvatarView avatar={basicAvatar()} size={280} /></div>
        )}
      </div>
    </div>
  );
}

function Message({ emoji, text, action }: { emoji: string; text: string; action?: React.ReactNode }) {
  return (
    <div className="center-fill">
      <Bubble style={{ maxWidth: 360, textAlign: 'center' }}>
        <div style={{ fontSize: 56 }} aria-hidden="true">{emoji}</div>
        <p className="t-title-m" style={{ margin: '12px 0' }}>{text}</p>
        {action}
      </Bubble>
    </div>
  );
}
